import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminAppBody from '../../../../components/admin/AdminAppBody';
import PageHeading from '../../../../components/admin/common/PageHeading';
import useApiCallHooks from '../../../../hooks/useApiCallHooks';
import { apiRoutes } from '../../../../routes/api/apiRoutes';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { uiRoutes } from '../../../../routes/ui/uiRoutes';

const ModulesEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [response, loading, error, callAPI, statusCode] = useApiCallHooks();

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    topics: ['']
  });

  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    callAPI('get', `${apiRoutes.admin.modules}/${id}`);
  }, [id]);

  useEffect(() => {
    if (response?.data && statusCode === 200) {
      const mod = response.data;
      setFormData({
        name: mod.name || '',
        type: mod.type || '',
        topics: Array.isArray(mod.topics)
          ? mod.topics.map(t => typeof t === 'string' ? t : t.name || '')
          : ['']
      });
    }
  }, [response, statusCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTopicChange = (index, value) => {
    const updated = [...formData.topics];
    updated[index] = value;
    setFormData({ ...formData, topics: updated });
  };

  const addTopic = () => {
    setFormData({ ...formData, topics: [...formData.topics, ''] });
  };

  const removeTopic = (index) => {
    const updated = [...formData.topics];
    updated.splice(index, 1);
    setFormData({ ...formData, topics: updated });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const cleanedData = {
      name: formData.name.trim(),
      type: formData.type.trim(),
      topics: formData.topics.filter(t => t.trim() !== '')
    };

    if (!cleanedData.name || !cleanedData.type || cleanedData.topics.length === 0) {
      toast.error('Please fill all fields including at least one topic.');
      return;
    }

    const formSubmitData = new FormData();
    formSubmitData.append('name', cleanedData.name);
    formSubmitData.append('type', cleanedData.type);
    cleanedData.topics.forEach((topic, index) => {
      formSubmitData.append(`topics[${index}]`, topic);
    });
    formSubmitData.append('_method', 'PUT');

    callAPI('post', `${apiRoutes.admin.modules}/${id}`, formSubmitData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  useEffect(() => {
    if (response?.data && statusCode === 200 && submitted) {
      toast.success('Module updated successfully!');
      setTimeout(() => navigate(uiRoutes.admin.modules.list), 2000);
    }
  }, [response, statusCode, submitted, navigate]);

  return (
    <AdminAppBody
      loading={loading}
      content={
        <div className="container card mt-4 p-4">
          <PageHeading title="Edit Module" subTitle="Update training module details" />
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Module Name</label>
                <input
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Type</label>
                <input
                  name="type"
                  className="form-control"
                  value={formData.type}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Topics</label>
              {formData.topics.map((topic, index) => (
                <div key={index} className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    value={topic}
                    onChange={(e) => handleTopicChange(index, e.target.value)}
                  />
                  {formData.topics.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeTopic(index)}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addTopic}
              >
                <FaPlus className="me-1" /> Add Topic
              </button>
            </div>

            <div className="text-end">
              <button type="submit" className="btn btn-primary mb-2">
                Update Module
              </button>
            </div>
          </form>
        </div>
      }
    />
  );
};

export default ModulesEdit;
